import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Handshake } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRole";

type HelpKind = "legal" | "capital" | "build" | "distribution" | "mentorship" | "other";

const KINDS: { value: HelpKind; label: string }[] = [
  { value: "legal", label: "Legal" },
  { value: "capital", label: "Capital / Funding" },
  { value: "build", label: "Build / Tech" },
  { value: "distribution", label: "Distribution / Marketing" },
  { value: "mentorship", label: "Mentorship" },
  { value: "other", label: "Other" },
];

export default function ExpressInterestDialog({ ideaId, trigger }: { ideaId: string; trigger?: React.ReactNode }) {
  const { user } = useAuth();
  const { partnerOrgIds } = useRoles();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<HelpKind>("mentorship");
  const [orgId, setOrgId] = useState<string>(partnerOrgIds[0] ?? "none");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!user) {
      toast.error("Please sign in first.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("idea_partner_interests").insert({
      idea_id: ideaId,
      offered_by_user_id: user.id,
      partner_org_id: orgId !== "none" ? orgId : null,
      help_kind: kind,
      message: message.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Could not send", { description: error.message });
      return;
    }
    toast.success("Interest sent. The idea author will see it.");
    setOpen(false);
    setMessage("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="gap-2">
            <Handshake className="h-3.5 w-3.5" /> Express Interest
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Express interest in this idea</DialogTitle>
          <DialogDescription>
            Tell the author what concrete help you can offer. They'll get a notification and decide whether to accept.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {partnerOrgIds.length > 0 && (
            <div>
              <Label>Offering as</Label>
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Just me (individual)</SelectItem>
                  {partnerOrgIds.map((id) => (
                    <SelectItem key={id} value={id}>Partner org</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Type of help</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as HelpKind)}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="msg">Message to author</Label>
            <Textarea
              id="msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Be specific about what you can do, your relevant experience, and any constraints."
              className="mt-1.5 min-h-[120px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "Sending…" : "Send interest"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}